import { useCallback } from 'react'

import type { IconColorName, IconName } from '@buffer-mono/popcorn'
import { toast } from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useSavedViewTracking } from '~publish/hooks/useSavedViewTracking'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { writeSavedViewsList } from './savedViewsCache'
import type { SavedView } from './useSavedViewsQuery'
import { missingOrganizationOutcome } from './utils'
import { CreateSavedViewMutation } from '../graphql/CreateSavedViewMutation'
import { SavedViewsQuery } from '../graphql/SavedViewsQuery'

type CreateSavedViewArgs = {
  name: string
  section: SavedView['section']
  view?: string | null
  filters: Record<string, unknown>
  icon: IconName
  color: IconColorName
}

type CreateSavedViewMutationResult = ReturnType<
  typeof useTypedMutation<
    'SavedView',
    ResultOf<typeof CreateSavedViewMutation>['createSavedView'],
    ResultOf<typeof CreateSavedViewMutation>,
    VariablesOf<typeof CreateSavedViewMutation>
  >
>

type UseCreateSavedViewMutationResult = readonly [
  (
    args: CreateSavedViewArgs,
  ) => Promise<Awaited<ReturnType<CreateSavedViewMutationResult[0]>>>,
  CreateSavedViewMutationResult[1],
]

/**
 * Create a saved view with optimistic update and success/error toasts.
 * Appends the created view to the end of the cached list to match server order.
 */
export function useCreateSavedViewMutation(): UseCreateSavedViewMutationResult {
  const organizationId = useCurrentOrganization()?.id
  const { trackCreated } = useSavedViewTracking()

  const [mutate, result] = useTypedMutation(
    CreateSavedViewMutation,
    (data) => data.createSavedView,
    {
      successTypename: 'SavedView',
      refetchQueries: [SavedViewsQuery],
      awaitRefetchQueries: true,
    },
  )

  const createSavedView = useCallback(
    async (args: CreateSavedViewArgs) => {
      if (!organizationId) return missingOrganizationOutcome()

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const nowIso = new Date().toISOString()

      const outcome = await mutate({
        variables: {
          input: {
            organizationId,
            name: args.name,
            section: args.section,
            view: args.view ?? null,
            filters: args.filters,
            icon: args.icon,
            color: args.color,
          },
        },
        optimisticResponse: {
          createSavedView: {
            __typename: 'SavedView',
            id: tempId,
            name: args.name,
            section: args.section,
            view: args.view ?? null,
            filters: args.filters,
            icon: args.icon,
            color: args.color,
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        },
        update: (cache, { data }) => {
          if (!isOfType(data?.createSavedView, 'SavedView')) return

          const createdView = data.createSavedView
          const isOptimistic = createdView.id === tempId

          writeSavedViewsList(cache, organizationId, (current) =>
            isOptimistic
              ? [...current, createdView]
              : current.map((v) => (v.id === tempId ? createdView : v)),
          )
        },
      })

      if (outcome.success) {
        toast.success('View saved')
        // Fire after server confirmation so the tracked viewId is the real
        // server id, not the optimistic temp-… placeholder.
        trackCreated({
          id: outcome.data.id,
          section: outcome.data.section,
          view: outcome.data.view,
        })
      } else if (!isOfType(outcome.error, 'LimitReachedError')) {
        // The Save dialog owns the limit-reached state (it opens a dedicated
        // limit view and lists deletable views), so don't double-surface it
        // as a generic error toast — let the caller react to the outcome.
        toast.error(outcome.error.message)
      }

      return outcome
    },
    [mutate, organizationId, trackCreated],
  )

  return [createSavedView, result] as const
}
